import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import IconBox from "@components/atoms/IconBox";
import { StyledRadio } from "@components/atoms/Radio";
import Table from "@components/atoms/Table";
import BaseModal from "@components/molecules/Modal/BaseModal";
import useGlobalModal from "@hooks/useGlobalModal";
import { type BannerCreateInitValuesType } from "@pages/BannerCreate/Data";
import { type ModalType } from "@store/modal";
import { type ColDef, type ICellRendererParams } from "ag-grid-community";
import { Radio } from "antd";
import { type FormikContextType } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { v2NoticesControllerFetchNotices, v2EventsControllerFetchEvents, type V2NoticeListResponseDto, type V2EventListResponseDto, V2BannerCreateRequestDtoLinkType as LinkType } from "@everon-it/api-client/contents-gw";
import OneButtonModal from "../OneButtonModal";
import { CloseIconWrap, ContentTitle, FooterButtonWrap, HeaderTitle, ModalContents, ModalFooter, ModalHeader } from "../OrderManagementModal/style";
import { columnDefs } from "./categoryTableColumnDefs";

type ConnectionListItem = V2NoticeListResponseDto | V2EventListResponseDto;
type SelectableData = ConnectionListItem;
type TableRowData = (SelectableData & { publishDuration: string })[];

export type ConnectionModalProps = {
    category: LinkType;
    modalType?: ModalType;
    modalHeaderTitle?: string;
    contentTitle?: string;
    firstButtonText?: string;
    secondButtonText?: string;
    tableColumnDefs?: ColDef[];
    onClickFirstButton?: () => void;
    onClickSecondButton?: () => void;
    handleClose?: () => void;
    formik: FormikContextType<BannerCreateInitValuesType>;
};

const ConnectionModal: React.FC<ConnectionModalProps> = ({ category, modalType = "ConnectionModal", modalHeaderTitle, contentTitle, firstButtonText, secondButtonText, handleClose, formik }) => {
    const { hideModal } = useGlobalModal();
    const [selectData, setSelectData] = useState<SelectableData>();
    const [isMismatchModal, setIsMismatchModal] = useState(false);
    const [isMissingModal, setIsMissingModal] = useState(false);

    const requestConnectionList = async (category: LinkType): Promise<ConnectionListItem[]> => {
        const params = { isVisible: true, pageSize: 1000 };
        switch (category) {
            case LinkType.NOTICE: {
                const response = await v2NoticesControllerFetchNotices(params);
                return response.rows;
            }
            case LinkType.EVENT: {
                const response = await v2EventsControllerFetchEvents(params);
                return response.rows;
            }
            default:
                return [];
        }
    };

    const { data } = useQuery({
        queryKey: ["connectionModal", category],
        queryFn: () => requestConnectionList(category),
        enabled: category === LinkType.NOTICE || category === LinkType.EVENT,
    });

    const handleSelectData = useCallback(
        (data: SelectableData) => {
            const { displayStartAt, displayEndAt } = formik.values;

            if (!displayEndAt) {
                setIsMissingModal(true);
                return;
            }

            // moment를 사용한 날짜 비교 (시간 무시, 날짜만 비교)
            const postStartDate = moment(displayStartAt).startOf("day");
            const contentsPostStartDate = moment(data.displayStartAt).startOf("day");
            const postEndDate = moment(displayEndAt).startOf("day");
            const contentsPostEndDate = moment(data.displayEndAt).startOf("day");

            const isPostStartAt = postStartDate.isSameOrAfter(contentsPostStartDate);
            const isPostEndAt = postEndDate.isSameOrBefore(contentsPostEndDate);

            if (!isPostStartAt || !isPostEndAt) {
                setIsMismatchModal(true);
            } else {
                setSelectData(data);
            }
        },
        [formik.values.displayStartAt, formik.values.displayEndAt],
    );

    const CustomCheckBox = useMemo(() => {
        return ({ data }: ICellRendererParams<SelectableData>) => {
            if (!data) {
                return null;
            }

            return (
                <Radio.Group onChange={() => handleSelectData(data)} value={selectData?.uuid}>
                    <StyledRadio key={data.uuid} value={data.uuid} />
                </Radio.Group>
            );
        };
    }, [handleSelectData, selectData]);

    const column = (category: LinkType): ColDef[] => {
        const defs: ColDef[] = category === LinkType.EVENT ? columnDefs[LinkType.EVENT] : columnDefs[LinkType.NOTICE];
        return defs.map(col => {
            if (col.field === "isSelect") {
                return { ...col, cellRenderer: CustomCheckBox };
            }
            return col;
        });
    };

    const connectionData = (): TableRowData | undefined => {
        return data?.map(item => ({
            ...item,
            publishDuration: `${item.displayStartAt} ~ ${item.displayEndAt}`,
        }));
    };

    const openPublishPeriodMismatchModal = () => {
        return <OneButtonModal title={`게시기간이 올바르지 않아요.\n다시 확인해주세요.`} handleClose={() => setIsMismatchModal(false)} onConfirm={() => setIsMismatchModal(false)} />;
    };
    // 게시 종료 기간 누락 모달
    const openPublishPeriodMissingModal = () => {
        return (
            <OneButtonModal
                title={`게시기간을 먼저 선택해주세요.`}
                handleClose={() => {
                    setIsMissingModal(false);
                    hideModal();
                }}
                onConfirm={() => {
                    setIsMissingModal(false);
                    hideModal();
                }}
            />
        );
    };

    return (
        <>
            <BaseModal isOpen handleClose={handleClose} modalWidth={65} disableModalPadding modalType={modalType}>
                <div>
                    <ModalHeader>
                        <HeaderTitle>{modalHeaderTitle || "modalHeaderTitle"}</HeaderTitle>
                        <CloseIconWrap>
                            <IconBox src={ICONS.MODAL_ICON.CLOSE} width={24} height={24} isCursorPointer onClick={handleClose} />
                        </CloseIconWrap>
                    </ModalHeader>
                    <ModalContents>
                        <ContentWrap>
                            <StyledContentTitle>{contentTitle || "contentTitle"}</StyledContentTitle>
                            <div style={{ height: "100%" }}>
                                <Table tableContentsInfoData={column(category)} rowData={connectionData() || []} onRowClick={handleSelectData} />
                            </div>
                        </ContentWrap>
                    </ModalContents>
                    <ModalFooter>
                        <FooterButtonWrap>
                            <Button
                                style={{ marginRight: 8 }}
                                text={firstButtonText || "firstButtonText"}
                                buttonSize="lager"
                                buttonType="text"
                                onClick={() => {
                                    hideModal();
                                }}
                            />
                            <Button
                                text={secondButtonText || "secondButtonText"}
                                buttonSize="lager"
                                onClick={() => {
                                    hideModal();
                                    formik.setFieldValue("linkTargetId", selectData?.uuid);
                                    formik.setFieldValue("linkTargetTitle", selectData?.title);
                                    formik.setFieldValue("linkTargetDisplayStartAt", selectData?.displayStartAt);
                                    formik.setFieldValue("linkTargetDisplayEndAt", selectData?.displayEndAt);
                                }}
                            />
                        </FooterButtonWrap>
                    </ModalFooter>
                </div>
            </BaseModal>
            {isMismatchModal && openPublishPeriodMismatchModal()}
            {isMissingModal && openPublishPeriodMissingModal()}
        </>
    );
};

export default ConnectionModal;

const StyledContentTitle = styled(ContentTitle)`
    padding-left: 0;
    margin-bottom: 12px;
`;

const ContentWrap = styled.div`
    width: 100%;
    height: 600px;
`;
